package com.evoapps.evoshift.data.dto.roster

import com.evoapps.evoshift.data.dto.system.MetadataDto
import kotlinx.serialization.Serializable

@Serializable
data class ShiftDto(
    val id: String,
    val rosterId: String,
    val date: String,
    val dailyRecordId: String,
    val isPublished: Boolean,
    val hasUnpublishedChanges: Boolean,
    val version: Int,
    /** SH-271: the published assignee before a reassignment since the last publish (older apps drop it). */
    val publishedUserId: String? = null,
    val conflictMetadata: ConflictMetadataDto? = null,
    val requiredRole: String?, // e.g., "Barista" - Who are we looking for?
    val requiredSkills: List<String> = emptyList(), // e.g., ["KeyHolder"]
    val userId: String? = null,
    val actualUserId: String? = null,
    val swapRequest: SwapRequestDto? = null,
    val isOpen: Boolean = false,
    val planned: ShiftTimeDto,
    val actual: ShiftTimeDto,
    val metadata: MetadataDto,
    val absenceId: String? = null,
    val tags: List<String> = emptyList(),
    /** SH-128: a manager recorded that nobody worked this unassigned shift. */
    val notWorked: Boolean = false,
    /** SH-134: an unplanned clock-in on this shift; written only by the clock functions and reviewClock. */
    val unplanned: UnplannedDto? = null,
    /** SH-236: why nobody came to an assigned shift (managers only; older apps skip it). */
    val noShowReason: String? = null,
    /** SH-236: one part of a split shift (managers only; older apps skip it and show both parts). */
    val split: ShiftSplitDto? = null,
    /** SH-267: the planned person is borrowed from this workplace of the group (older apps skip it; the server restores it). */
    val borrowedFromWorkplaceId: String? = null,
)

/** SH-236: `shifts/{id}.split`, the link between the two parts of a split shift. */
@Serializable
data class ShiftSplitDto(
    val groupId: String,
    val part: Int,
    val handoverAt: String,
    val originalPlannedEnd: String,
    val originalActualEnd: String? = null,
    val reason: String? = null,
    val sickness: Boolean = false,
    val otherUserId: String? = null,
    val byUserId: String = "",
    val at: String = ""
)

/** SH-134: `shifts/{id}.unplanned` (functions/clockReview.js). */
@Serializable
data class UnplannedDto(
    val kind: String,
    val userId: String,
    val userName: String? = null,
    val coveredUserId: String? = null,
    val coveredUserName: String? = null,
    val status: String,
    val via: String? = null,
    val at: String = "",
    val decidedBy: String? = null,
    val decidedByName: String? = null,
    val decidedAt: String? = null
)

/** SH-134: `actual.review`, a manager's decision on flagged clock times (reviewClock only). */
@Serializable
data class ClockReviewDto(
    val decision: String,
    val flags: List<String> = emptyList(),
    val start: String = "",
    val end: String = "",
    val by: String = "",
    val byName: String? = null,
    val at: String = ""
)

@Serializable
data class SwapRequestDto(
    val status: String,
    val initiatorUserId: String,
    val initiatorName: String? = null,
    val candidateUserId: String? = null,
    val candidateName: String? = null,
    val managerNote: String? = null,
    val createdAt: String,
    /** SH-165: the initiator's optional note to the manager and the colleague. */
    val initiatorNote: String? = null
)

@Serializable
enum class SwapRequestStatusDto {
    REQUESTED_BY_OWNER,
    AVAILABLE,
    PENDING_FINAL_APPROVAL,
    REJECTED
}

@Serializable
enum class ShiftTagsDto {
    NIGHT_SHIFT,
    HOLIDAY_RATE,
    OVERTIME
}


@Serializable
data class ConflictMetadataDto(
    val isForced: Boolean,
    val conflictStatus: String,
    val conflictingShiftId: String? = null
)

/**
 * Planned or actual times ("YYYY-MM-DDTHH:mm:ss", local). On `actual`, SH-107 adds how each end was
 * recorded: source APP | KIOSK | MANAGER, the tablet's id, and for an event queued offline on the
 * tablet syncedLate + the device's time. KIOSK stamps are written only by the kioskClock function.
 */
@Serializable
data class ShiftTimeDto(
    val start: String,
    val end: String,
    val startSource: String? = null,
    val startKioskId: String? = null,
    val startSyncedLate: Boolean? = null,
    val startDeviceTime: String? = null,
    val endSource: String? = null,
    val endKioskId: String? = null,
    val endSyncedLate: Boolean? = null,
    val endDeviceTime: String? = null,
    /**
     * SH-106: the geofence check of each end (INSIDE, OUTSIDE, UNVERIFIED) and the distance from
     * the workplace in whole metres. Written only by the clockShift function; never coordinates.
     */
    val startLocationCheck: String? = null,
    val startDistanceM: Int? = null,
    val endLocationCheck: String? = null,
    val endDistanceM: Int? = null,
    /**
     * SH-116: breaks while clocked in (actual only). The person's own breaks are written by the
     * clock functions (clockShift, kioskClock, clockWithQr); managers correct them directly.
     */
    val breaks: List<ShiftBreakDto>? = null,
    /**
     * SH-134: flags on the clock times (functions/clockReview.js), the manager's decision, and the
     * Storage paths of the tablet's clock-in / clock-out photos. Never written by the person.
     */
    val flags: List<String>? = null,
    val review: ClockReviewDto? = null,
    val startPhoto: String? = null,
    val endPhoto: String? = null,
    /** SH-180: a self-reported time's distance from the server's clock (functions/clockReview.js). */
    val startDrift: ClockDriftDto? = null,
    val endDrift: ClockDriftDto? = null,
    /** SH-257: who entered these times themselves and when (functions/workHours.js only). */
    val reportedBy: String? = null,
    val reportedAt: String? = null
)

/** SH-180: the server's time of the write and the written time's minutes ahead of it (negative: behind). */
@Serializable
data class ClockDriftDto(val serverAt: String? = null, val minutes: Int? = null)

/** One break: local ISO start and end ("" while it goes on), and where it was started. */
@Serializable
data class ShiftBreakDto(
    val start: String,
    val end: String = "",
    val source: String? = null,
    /** SH-106 checks where the workplace has a geofence (written by clockShift only). */
    val startLocationCheck: String? = null,
    val startDistanceM: Int? = null,
    val endLocationCheck: String? = null,
    val endDistanceM: Int? = null
)
